import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ContactFormCommunicationChannel as Channel } from 'graphql-ts-types';
import type { HeightProps } from 'styled-system';
import { color, height, space, themeGet } from 'styled-system';

import { Flex } from 'common/components/Flex';
import TelField from 'common/components/Form/TelField';
import type { TelFieldProps } from 'common/components/Form/TelField/index';
import { Radio } from 'common/components/Radio';
import SelectCard from 'common/components/SelectCard';
import { FieldChangeTrigger } from 'common/enums/form';
import type { ColorProps, SpaceProps } from 'common/theme/system-types';
import type { FlexGrowProps } from 'common/theme/system-utilities';
import { flexGrow } from 'common/theme/system-utilities';
import type { FormGroupData } from 'common/types/field';
import type { FieldChangeTriggerHandler } from 'common/types/form';
import type { DispatchState } from 'common/types/React';
import ChannelLabel from './ChannelLabel';

export type ChannelListFormData = FormGroupData<ChannelListProps>;
interface ChannelListProps<FieldName extends string = string> {
  name: FieldName;
  value?: string;
  disabled?: boolean;
  ariaLabelledBy?: string;
  telFieldProps?: TelFieldProps;
  shouldHideCallbackOption?: boolean;
  onChange: FieldChangeTriggerHandler<FieldName>;
  onSelectChannel?: (c: Channel) => void;
  setCallbackNotAvailableError?: DispatchState<boolean>;
}

const DEFAULT_ICON_SIZE = 24;

const CHANNEL_OPTIONS = [
  {
    channelId: Channel.Email,
    label: 'h24_channel_option_email_label',
    description: 'h24_channel_option_email_description',
  },
  {
    channelId: Channel.Callback,
    label: 'h24_channel_option_callback_label',
    description: 'h24_channel_option_callback_description',
  },
];

const Container = styled(Flex)`
  small {
    margin-top: ${themeGet('space.sp_4')}px;
  }
`;

const RadioInput = styled(Radio)<SpaceProps & ColorProps & FlexGrowProps & HeightProps>`
  ${space};
  ${color};
  ${flexGrow};
  ${height};
`;

const radioInputStyles: Partial<ComponentProps<typeof RadioInput>> = {
  mr: 'sp_8',
  mb: 'sp_0',
  p: 'sp_0',
  color: 'sys.neutral.border.hover',
  flexGrow: '0',
  height: '100%',
  iconSize: DEFAULT_ICON_SIZE,
};

const StyledCardContainer = styled(SelectCard)`
  &:focus-within input[type='radio']:focus-visible + label > svg {
    outline: none;
    border: none;
  }
`;

const ChannelList = ({
  name,
  value,
  disabled,
  telFieldProps,
  shouldHideCallbackOption,
  onChange,
  ariaLabelledBy,
  onSelectChannel,
  setCallbackNotAvailableError,
}: ChannelListProps) => {
  const selected = value as Channel;
  const handleChange = (newValue: Channel) => {
    if (setCallbackNotAvailableError) {
      if (newValue === Channel.Email) {
        setCallbackNotAvailableError(false);
      }
    }

    if (onSelectChannel) {
      onSelectChannel(newValue);
    }

    onChange({
      fieldName: name,
      value: newValue,
      trigger: FieldChangeTrigger.Change,
    });
  };

  return (
    <Container width="1" flexDirection="column" role="radiogroup" aria-labelledby={ariaLabelledBy}>
      {CHANNEL_OPTIONS.map(({ channelId, ...restProps }) => {
        const isSelected = selected === channelId;
        const isCallback = channelId === Channel.Callback;
        const shouldRenderTelField = telFieldProps && isCallback && selected === Channel.Callback;

        const label = (
          <Flex flexDirection="column">
            <ChannelLabel {...restProps} />
            {shouldRenderTelField && (
              <Flex mt="sp_16" flexDirection="column">
                <TelField {...telFieldProps} />
              </Flex>
            )}
          </Flex>
        );

        if (isCallback && shouldHideCallbackOption) {
          return;
        }

        const onChangeHandler = () => {
          handleChange(channelId);
        };

        return (
          <StyledCardContainer
            key={channelId}
            selected={isSelected}
            onClick={onChangeHandler}
            data-testid={`channel-${channelId}`}
          >
            <RadioInput
              {...radioInputStyles}
              name={name}
              label={label}
              value={channelId}
              disabled={disabled}
              onChange={onChangeHandler}
              defaultChecked={isSelected}
            />
          </StyledCardContainer>
        );
      })}
    </Container>
  );
};

export default ChannelList;
